/**
 * Admin Location Manager - CMS page for managing office locations
 */
import { useState, useEffect } from 'react';
import { useAdminTheme } from '@/context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import PublishStatusCard from '@/components/admin/PublishStatusCard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/admin/AdminLayout';
import {
  MapPin,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  Search,
  ExternalLink,
  RefreshCw
} from 'lucide-react';

const API_URL = window.location.origin || '';

const AdminLocationManager = () => {
  const { isDark } = useAdminTheme();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    city: '',
    area: '',
    state: '',
    slug: '',
    tagline: '',
    description: '',
    keywords: '',
    address: {
      line1: 'UniXperts Education Consultancy',
      line2: '',
      city: '',
      state: '',
      pincode: '',
      landmark: ''
    },
    phone: '+91-7273928928',
    email: '',
    timings: 'Mon-Sat: 10:00 AM - 7:00 PM',
    map_url: '',
    coordinates: { lat: 0, lng: 0 },
    local_focus: [],
    popular_destinations: ['USA', 'Canada', 'UK', 'Australia'],
    unique_content: '',
    is_active: true
  });

  // Fetch locations
  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/locations/admin/all`);
      if (res.ok) {
        const data = await res.json();
        setLocations(data);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  // Seed initial data
  const seedLocations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/locations/admin/seed`, {
        method: 'POST'
      });
      const data = await res.json();
      toast.success(data.message);
      fetchLocations();
    } catch (error) {
      console.error('Error seeding locations:', error);
      toast.error('Failed to seed locations');
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  // Generate slug from city and area
  const generateSlug = (city, area) => {
    let slug = city.toLowerCase().replace(/\s+/g, '-');
    if (area) {
      slug += '-' + area.toLowerCase().replace(/\s+/g, '-');
    }
    return slug;
  };

  // Handle form input change
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-generate slug when city or area changes
      if (field === 'city' || field === 'area') {
        const city = field === 'city' ? value : prev.city;
        const area = field === 'area' ? value : prev.area;
        newData.slug = generateSlug(city, area);
        
        // Also update email
        if (field === 'city' && value) {
          newData.email = `${value.toLowerCase().replace(/\s+/g, '')}@unixperts.com`;
        }
      }
      
      return newData;
    });
  };

  // Handle address field change
  const handleAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  // Handle local focus (comma-separated to array)
  const handleLocalFocusChange = (value) => {
    const items = value.split('\n').map(s => s.trim()).filter(s => s);
    setFormData(prev => ({ ...prev, local_focus: items }));
  };

  // Handle popular destinations
  const handleDestinationsChange = (value) => {
    const items = value.split(',').map(s => s.trim()).filter(s => s);
    setFormData(prev => ({ ...prev, popular_destinations: items }));
  };

  // Open dialog for new location
  const handleAddNew = () => {
    setEditingLocation(null);
    setFormData({
      city: '',
      area: '',
      state: '',
      slug: '',
      tagline: '',
      description: '',
      keywords: '',
      address: {
        line1: 'UniXperts Education Consultancy',
        line2: '',
        city: '',
        state: '',
        pincode: '',
        landmark: ''
      },
      phone: '+91-7273928928',
      email: '',
      timings: 'Mon-Sat: 10:00 AM - 7:00 PM',
      map_url: '',
      coordinates: { lat: 0, lng: 0 },
      local_focus: [],
      popular_destinations: ['USA', 'Canada', 'UK', 'Australia'],
      unique_content: '',
      is_active: true
    });
    setIsDialogOpen(true);
  };

  // Open dialog for editing
  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      city: location.city || '',
      area: location.area || '',
      state: location.state || '',
      slug: location.slug || '',
      tagline: location.tagline || '',
      description: location.description || '',
      keywords: location.keywords || '',
      address: location.address || {
        line1: 'UniXperts Education Consultancy',
        line2: '',
        city: '',
        state: '',
        pincode: '',
        landmark: ''
      },
      phone: location.phone || '+91-7273928928',
      email: location.email || '',
      timings: location.timings || 'Mon-Sat: 10:00 AM - 7:00 PM',
      map_url: location.map_url || '',
      coordinates: location.coordinates || { lat: 0, lng: 0 },
      local_focus: location.local_focus || [],
      popular_destinations: location.popular_destinations || ['USA', 'Canada', 'UK', 'Australia'],
      unique_content: location.unique_content || '',
      is_active: location.is_active !== false
    });
    setIsDialogOpen(true);
  };

  // Save location
  const handleSave = async () => {
    if (!formData.city || !formData.state || !formData.slug) {
      toast.error('City, State, and Slug are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingLocation
        ? `${API_URL}/api/locations/admin/${editingLocation.id}`
        : `${API_URL}/api/locations/admin`;
      
      const method = editingLocation ? 'PUT' : 'POST';
      
      // Prepare payload
      const payload = {
        ...formData,
        address: {
          ...formData.address,
          city: formData.address.city || formData.city,
          state: formData.address.state || formData.state
        }
      };

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(editingLocation ? 'Location updated' : 'Location created');
        setIsDialogOpen(false);
        fetchLocations();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save location');
      }
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error('Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  // Toggle active status
  const handleToggleActive = async (location) => {
    try {
      const res = await fetch(`${API_URL}/api/locations/admin/${location.id}/toggle-active`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success(`Location ${location.is_active ? 'deactivated' : 'activated'}`);
        fetchLocations();
      }
    } catch (error) {
      toast.error('Failed to toggle status');
    }
  };

  // Delete location
  const handleDelete = async (location) => {
    if (!window.confirm(`Are you sure you want to delete ${location.city}${location.area ? ` (${location.area})` : ''}?`)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/locations/admin/${location.id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Location deleted');
        fetchLocations();
      }
    } catch (error) {
      toast.error('Failed to delete location');
    }
  };

  // Filter locations
  const filteredLocations = locations.filter(loc =>
    loc.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    loc.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (loc.area && loc.area.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Location Manager
          </h1>
          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
            Manage office locations for local SEO
          </p>
        </div>
        <div className="flex gap-2">
          {locations.length === 0 && (
            <Button variant="outline" onClick={seedLocations}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Seed Initial Data
            </Button>
          )}
          <Button onClick={handleAddNew} className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white">
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
        <Input
          placeholder="Search locations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`pl-10 ${isDark ? 'bg-white/5 border-white/10' : ''}`}
        />
      </div>

      {/* Locations Table */}
      <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-ux-blue" />
            </div>
          ) : filteredLocations.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
              <p className={isDark ? 'text-white/60' : 'text-slate-500'}>
                {searchTerm ? 'No locations found' : 'No locations yet. Click "Seed Initial Data" to add default locations.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-white/10' : ''}>
                  <TableHead className={isDark ? 'text-white/70' : ''}>City</TableHead>
                  <TableHead className={isDark ? 'text-white/70' : ''}>State</TableHead>
                  <TableHead className={isDark ? 'text-white/70' : ''}>Slug</TableHead>
                  <TableHead className={isDark ? 'text-white/70' : ''}>Status</TableHead>
                  <TableHead className={`text-right ${isDark ? 'text-white/70' : ''}`}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLocations.map((location) => (
                  <TableRow key={location.id} className={isDark ? 'border-white/10' : ''}>
                    <TableCell className={isDark ? 'text-white' : ''}>
                      <div className="font-medium">
                        {location.city}
                        {location.area && (
                          <span className={`text-sm ml-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                            ({location.area})
                          </span>
                        )}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                        {location.email}
                      </div>
                    </TableCell>
                    <TableCell className={isDark ? 'text-white/80' : ''}>{location.state}</TableCell>
                    <TableCell>
                      <code className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-white/10 text-white/70' : 'bg-slate-100'}`}>
                        /{location.slug}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge variant={location.is_active ? 'default' : 'secondary'}>
                        {location.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => window.open(`/locations/${location.slug}`, '_blank')}
                          title="Preview"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleActive(location)}
                          title={location.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {location.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(location)}
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(location)}
                          className="text-red-500 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className={`max-w-3xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#1a2830] border-white/10' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>
              {editingLocation ? 'Edit Location' : 'Add New Location'}
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-white/60' : ''}>
              {editingLocation ? 'Update office location details' : 'Add a new office location for local SEO'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-white' : ''}>City *</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="e.g., Bengaluru"
                  className={isDark ? 'bg-white/5 border-white/10' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-white' : ''}>Area/Branch (optional)</Label>
                <Input
                  value={formData.area}
                  onChange={(e) => handleInputChange('area', e.target.value)}
                  placeholder="e.g., Koramangala"
                  className={isDark ? 'bg-white/5 border-white/10' : ''}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-white' : ''}>State *</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  placeholder="e.g., Karnataka"
                  className={isDark ? 'bg-white/5 border-white/10' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-white' : ''}>URL Slug *</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  placeholder="e.g., bengaluru"
                  className={isDark ? 'bg-white/5 border-white/10' : ''}
                />
                <p className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                  URL: /locations/{formData.slug || 'slug'}
                </p>
              </div>
            </div>

            {/* SEO Fields */}
            <div className="space-y-2">
              <Label className={isDark ? 'text-white' : ''}>Tagline</Label>
              <Input
                value={formData.tagline}
                onChange={(e) => handleInputChange('tagline', e.target.value)}
                placeholder="e.g., From India's Silicon Valley to Global Tech Leaders"
                className={isDark ? 'bg-white/5 border-white/10' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label className={isDark ? 'text-white' : ''}>Meta Description (SEO)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Brief description for search engines (150-160 chars recommended)"
                rows={2}
                className={isDark ? 'bg-white/5 border-white/10' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label className={isDark ? 'text-white' : ''}>SEO Keywords</Label>
              <Textarea
                value={formData.keywords}
                onChange={(e) => handleInputChange('keywords', e.target.value)}
                placeholder="study abroad consultants Bengaluru, MS USA Bangalore, MBA abroad Karnataka..."
                rows={2}
                className={isDark ? 'bg-white/5 border-white/10' : ''}
              />
            </div>

            {/* Address */}
            <div className="space-y-4">
              <Label className={`text-lg font-semibold ${isDark ? 'text-white' : ''}`}>Office Address</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-white/80' : ''}>Address Line 2</Label>
                  <Input
                    value={formData.address.line2}
                    onChange={(e) => handleAddressChange('line2', e.target.value)}
                    placeholder="e.g., Koramangala 5th Block"
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? 'text-white/80' : ''}>Pincode</Label>
                  <Input
                    value={formData.address.pincode}
                    onChange={(e) => handleAddressChange('pincode', e.target.value)}
                    placeholder="e.g., 560034"
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-white/80' : ''}>Landmark</Label>
                <Input
                  value={formData.address.landmark}
                  onChange={(e) => handleAddressChange('landmark', e.target.value)}
                  placeholder="e.g., Near Forum Mall"
                  className={isDark ? 'bg-white/5 border-white/10' : ''}
                />
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-white' : ''}>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={isDark ? 'bg-white/5 border-white/10' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-white' : ''}>Email</Label>
                <Input
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="e.g., bangalore@unixperts.com"
                  className={isDark ? 'bg-white/5 border-white/10' : ''}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className={isDark ? 'text-white' : ''}>Timings</Label>
              <Input
                value={formData.timings}
                onChange={(e) => handleInputChange('timings', e.target.value)}
                className={isDark ? 'bg-white/5 border-white/10' : ''}
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label className={isDark ? 'text-white' : ''}>Local Focus Points (one per line)</Label>
              <Textarea
                value={formData.local_focus.join('\n')}
                onChange={(e) => handleLocalFocusChange(e.target.value)}
                placeholder="IT professionals from Whitefield & Electronic City&#10;Engineering students from RV, BMS & PES&#10;Students from Koramangala, Indiranagar & HSR"
                rows={4}
                className={isDark ? 'bg-white/5 border-white/10' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label className={isDark ? 'text-white' : ''}>Popular Destinations (comma-separated)</Label>
              <Input
                value={formData.popular_destinations.join(', ')}
                onChange={(e) => handleDestinationsChange(e.target.value)}
                placeholder="USA, Canada, UK, Germany"
                className={isDark ? 'bg-white/5 border-white/10' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label className={isDark ? 'text-white' : ''}>Unique Content</Label>
              <Textarea
                value={formData.unique_content}
                onChange={(e) => handleInputChange('unique_content', e.target.value)}
                placeholder="City-specific content that makes this location unique..."
                rows={4}
                className={isDark ? 'bg-white/5 border-white/10' : ''}
              />
            </div>

            {/* Active Status */}
            <PublishStatusCard
              isPublished={formData.is_active}
              onChange={(checked) => handleInputChange('is_active', checked)}
              isDark={isDark}
              entityLabel="location"
              publicRoute="/locations"
              testId="location-publish-toggle"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingLocation ? 'Update Location' : 'Create Location'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminLocationManager;
